/**
 * createSharedCache — Generic factory for cross-tab synced list caches.
 *
 * Önceden `useDepos` içinde özel-yapım olarak bulunan akış (module cache +
 * subscribers + BroadcastChannel + window focus refetch) generic bir factory'e
 * çıkarıldı (Haz 13, 2026). Şimdi yeni cache'li hook'lar tek satırda
 * üretilebiliyor:
 *
 *   const { useShared, invalidate } = createSharedCache({
 *     channel: 'firms-cache',
 *     fetcher: () => api.get('/firms').then(r => r.data?.data || []),
 *   });
 *
 *   export const useFirms = useShared;
 *   export const invalidateFirms = invalidate;
 *
 * Garantiler:
 *   - Tek bir HTTP isteği: aynı anda 10 component mount etse de tek fetch.
 *   - Sekme-içi invalidation: invalidate() çağrısı bu sekmedeki tüm
 *     hook'ları yenisi gelene kadar refetch tetikler.
 *   - Sekmeler arası invalidation: BroadcastChannel ile aktif tüm
 *     sekmelere mesaj postalanır.
 *   - Focus refetch: sekmeye geri dönüldüğünde 5sn throttle ile silent
 *     refetch (BroadcastChannel desteklenmeyen ortamlar için yedek).
 *   - Değişiklik yoksa render trigger edilmez (JSON karşılaştırması).
 */
import { useState, useEffect, useCallback } from 'react';

export function createSharedCache({ channel, fetcher, initial = null }) {
  let cached = initial;
  let fetchPromise = null;
  const subscribers = new Set();

  // Cross-tab kanal
  let bc = null;
  try {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      bc = new BroadcastChannel(channel);
      bc.onmessage = (ev) => {
        if (ev?.data?.type === 'invalidate') {
          cached = null;
          fetchPromise = null;
          subscribers.forEach((fn) => { try { fn(); } catch { /* noop */ } });
        }
      };
    }
  } catch { /* desteklenmiyorsa sessiz geç */ }

  function loadFresh() {
    if (!fetchPromise) {
      fetchPromise = Promise.resolve(fetcher())
        .then((data) => { cached = data || []; return cached; })
        .catch(() => { cached = []; return cached; })
        .finally(() => { fetchPromise = null; });
    }
    return fetchPromise;
  }

  function invalidate() {
    cached = null;
    fetchPromise = null;
    subscribers.forEach((fn) => { try { fn(); } catch { /* noop */ } });
    try { bc && bc.postMessage({ type: 'invalidate', ts: Date.now() }); } catch { /* noop */ }
  }

  // Focus refetch — 5sn throttle
  if (typeof window !== 'undefined') {
    let lastFocusFetch = 0;
    window.addEventListener('focus', () => {
      const now = Date.now();
      if (cached && now - lastFocusFetch > 5000) {
        lastFocusFetch = now;
        Promise.resolve(fetcher())
          .then((data) => {
            const fresh = data || [];
            const changed = JSON.stringify(fresh) !== JSON.stringify(cached);
            cached = fresh;
            if (changed) {
              subscribers.forEach((fn) => { try { fn(); } catch { /* noop */ } });
            }
          })
          .catch(() => { /* silent */ });
      }
    });
  }

  function useShared() {
    const [data, setData] = useState(cached || []);
    const [loading, setLoading] = useState(!cached);

    const sync = useCallback(() => {
      if (cached) {
        setData(cached);
        setLoading(false);
        return;
      }
      setLoading(true);
      loadFresh().then((fresh) => {
        setData(fresh);
        setLoading(false);
      });
    }, []);

    useEffect(() => {
      sync();
      subscribers.add(sync);
      return () => { subscribers.delete(sync); };
    }, [sync]);

    const refresh = useCallback(async () => { invalidate(); }, []);
    return { data, loading, refresh };
  }

  return { useShared, invalidate };
}
